import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import "@/i18n";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { PageTitlesProvider } from "@/contexts/PageTitlesContext";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import ComingSoon from "@/pages/ComingSoon";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import Dashboard from "@/pages/Dashboard";
import OrderDetail from "@/pages/OrderDetail";
import OrderSuccess from "@/pages/OrderSuccess";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminOrgChart from "@/pages/admin/AdminOrgChart";
import Points from "@/pages/Points";
import AgentHub from "@/pages/AgentHub";
import Events from "@/pages/Events";
import Booking from "@/pages/Booking";
import BookingDetail from "@/pages/BookingDetail";
import { CrystalsList, CrystalArticle } from "@/pages/Crystals";
import Angelina from "@/pages/Angelina";
import Reports from "@/pages/Reports";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminArticles from "@/pages/admin/AdminArticles";
import AdminAngelina from "@/pages/admin/AdminAngelina";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminMasters from "@/pages/admin/AdminMasters";
import AdminPageTitles from "@/pages/admin/AdminPageTitles";
import AuthCallback from "@/pages/AuthCallback";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-16 text-[#4a6e77]">Loading...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function PublicRoute({ children }) {
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  // CRITICAL: synchronous OAuth callback detection — runs BEFORE any Route matches.
  // Detecting in useEffect would run after first render (too late for race-free auth).
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
            <Route path="/about" element={<PublicRoute><About /></PublicRoute>} />
            <Route path="/zero-space" element={<PublicRoute><Events /></PublicRoute>} />
            <Route path="/angelina" element={<PublicRoute><Angelina /></PublicRoute>} />
            <Route path="/crystals" element={<PublicRoute><CrystalsList /></PublicRoute>} />
            <Route path="/crystals/:id" element={<PublicRoute><CrystalArticle /></PublicRoute>} />
            <Route path="/booking" element={<PublicRoute><Booking /></PublicRoute>} />
            <Route path="/bookings/:id" element={<PublicRoute><RequireAuth><BookingDetail /></RequireAuth></PublicRoute>} />
            <Route path="/reports" element={<PublicRoute><RequireAuth><Reports /></RequireAuth></PublicRoute>} />

            <Route path="/products" element={<PublicRoute><Products /></PublicRoute>} />
            <Route path="/products/:id" element={<PublicRoute><ProductDetail /></PublicRoute>} />

            <Route path="/cart" element={<PublicRoute><Cart /></PublicRoute>} />
            <Route path="/checkout" element={<PublicRoute><RequireAuth><Checkout /></RequireAuth></PublicRoute>} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/profile" element={<PublicRoute><RequireAuth><Profile /></RequireAuth></PublicRoute>} />
            <Route path="/dashboard" element={<PublicRoute><RequireAuth><Dashboard /></RequireAuth></PublicRoute>} />
            <Route path="/points" element={<PublicRoute><RequireAuth><Points /></RequireAuth></PublicRoute>} />
            <Route path="/agent" element={<PublicRoute><RequireAuth><AgentHub /></RequireAuth></PublicRoute>} />
            <Route path="/orders/:id" element={<PublicRoute><RequireAuth><OrderDetail /></RequireAuth></PublicRoute>} />
            <Route path="/order-success/:id" element={<PublicRoute><RequireAuth><OrderSuccess /></RequireAuth></PublicRoute>} />

            <Route path="/admin" element={<AdminLayout />}>
      <Route index element={<Navigate to="/admin/analytics" replace />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="org-chart" element={<AdminOrgChart />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="masters" element={<AdminMasters />} />
              <Route path="page-titles" element={<AdminPageTitles />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="angelina" element={<AdminAngelina />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="analytics" element={<AdminAnalytics />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <PageTitlesProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
          </PageTitlesProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
